import React, { useState } from "react";
import api, { formatApiError, clean } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

/**
 * Generic request form. `fields` describe extra inputs.
 * type: visite | gestion | conciergerie | contact | investissement | service
 */
export const RequestForm = ({ type, title, subtitle, fields = [], defaults = {}, submitLabel, testid = "request-form" }) => {
  const { t } = useLang();
  const [data, setData] = useState({ name: "", phone: "", email: "", message: "", ...defaults });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setData((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = clean({ type, ...data });
      await api.post("/requests", payload);
      setDone(true);
      toast.success(t("form.success"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-xl bg-white border border-black/5 p-8 text-center" data-testid={`${testid}-success`}>
        <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-500" />
        <h3 className="mt-4 font-serif text-2xl font-semibold text-navy">{t("form.thankTitle")}</h3>
        <p className="mt-2 text-slate-600">{t("form.thankDesc")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={testid} className="rounded-xl bg-white border border-black/5 p-6 sm:p-8 space-y-4">
      {title && <h3 className="font-serif text-2xl font-semibold text-navy">{title}</h3>}
      {subtitle && <p className="text-sm text-slate-600 -mt-2">{subtitle}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label>{t("form.nameReq")}</Label>
          <Input required value={data.name} onChange={(e) => set("name", e.target.value)} data-testid={`${testid}-name`} className="mt-1.5" />
        </div>
        <div>
          <Label>{t("form.phoneReq")}</Label>
          <Input required value={data.phone} onChange={(e) => set("phone", e.target.value)} data-testid={`${testid}-phone`} className="mt-1.5" />
        </div>
      </div>
      <div>
        <Label>{t("form.email")}</Label>
        <Input type="email" value={data.email} onChange={(e) => set("email", e.target.value)} data-testid={`${testid}-email`} className="mt-1.5" />
      </div>
      {fields.map((fld) => (
        <div key={fld.name}>
          <Label>{fld.label}</Label>
          <Input value={data[fld.name] || ""} onChange={(e) => set(fld.name, e.target.value)} placeholder={fld.placeholder} data-testid={`${testid}-${fld.name}`} className="mt-1.5" type={fld.type || "text"} />
        </div>
      ))}
      <div>
        <Label>{t("form.message")}</Label>
        <Textarea value={data.message} onChange={(e) => set("message", e.target.value)} rows={4} data-testid={`${testid}-message`} className="mt-1.5" />
      </div>
      <Button type="submit" disabled={loading} data-testid={`${testid}-submit`} className="w-full bg-navy text-white hover:bg-navy-600 h-11 rounded-lg font-semibold">
        {loading ? t("form.sending") : (submitLabel || t("form.submitDefault"))}
      </Button>
    </form>
  );
};
